import { FunctionComponent } from "react";
import InfoPanel from "@components/form/InfoPanel";
import WorkflowPanels from "@components/form/WorkflowPanels";
import CreateBalanceAdjustmentEnterData from "@components/form/balance_adjustments/CreateBalanceAdjustmentEnterData";
import CreateBalanceAdjustmentSelectExtractionRight from "@components/form/balance_adjustments/CreateBalanceAdjustmentSelectExtractionRight";
import CreateBalanceAdjustmentSelectScheme from "@components/form/balance_adjustments/CreateBalanceAdjustmentSelectScheme";
import CreateBalanceAdjustmentSelectSubscriber from "@components/form/balance_adjustments/CreateBalanceAdjustmentSelectSubscriber";
import CreateBalanceAdjustmentUploadEvidence from "@components/form/balance_adjustments/CreateBalanceAdjustmentUploadEvidence";
import CreateBalanceAdjustmentLedger from "@components/form/balance_adjustments/CreateBalanceAdjustmentLedger";
import Layout from "@components/layout/Layout";
import StepButton from "@components/shared/StepButton";
import {
  BalanceAdjustmentProvider,
  useBalanceAdjustmentContext,
} from "@context/BalanceAdjustmentContext";
import { formatVolume } from "@utils/formatVolume";
import { useTranslation } from "react-i18next";
import CreateBalanceAdjustmentSelectExtractionPoint from "@components/form/balance_adjustments/CreateBalanceAdjustmentSelectExtractionPoint";
import CreateBalanceAdjustmentCompleted from "@components/form/balance_adjustments/CreateBalanceAdjustmentCompleted";
import { sumBy } from "lodash";
import { useSearchParams } from "react-router-dom";
import { useSubscriber } from "@hooks/query/useSubscriber";
import { useGetLevel1Resource } from "@hooks/query/useGetLevel1Resource";
import { BalanceAdjustmentType } from "@services/balanceAdjustment";

const CreateBalanceAdjustment: FunctionComponent = () => {
  return (
    <BalanceAdjustmentProvider>
      <MainComponent />
    </BalanceAdjustmentProvider>
  );
};

const MainComponent: FunctionComponent = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const {
    currentStep,
    stepHelpers,
    balanceAdjustmentData,
    setBalanceAdjustmentData,
  } = useBalanceAdjustmentContext();

  useSubscriber(searchParams.get("subscriberId")!, {
    enabled: !!searchParams.get("subscriberId"),
    refetchOnWindowFocus: false,
    onSuccess: (subscriber: any) => {
      if (subscriber) {
        setBalanceAdjustmentData({
          ...balanceAdjustmentData,
          type: BalanceAdjustmentType.WATER_HARVESTING,
          level1Resource: subscriber.level1WRS,
          subscriber,
        });
      }
    },
  });

  useGetLevel1Resource(searchParams.get("level1ResourceId")!, {
    enabled: !!searchParams.get("level1ResourceId"),
    refetchOnWindowFocus: false,
    onSuccess: (level1Resource: any) => {
      if (level1Resource) {
        setBalanceAdjustmentData({
          ...balanceAdjustmentData,
          level1Resource,
        });
      }
    },
  });

  return (
    <Layout
      permissions={["CreateBalanceAdjustments"]}
      breadcrumb={[
        {
          label: t("dashboard.dashboard"),
          href: "/polestar",
        },
        {
          label: t("balance_adjustment.water_harvesting.title"),
        },
      ]}
      accountingPeriodLevel1ResourceId={
        balanceAdjustmentData?.level1Resource?.id
      }
      title={t("balance_adjustment.water_harvesting.title")}
    >
      <WorkflowPanels
        selectedStep={currentStep}
        steps={[
          {
            label: (
              <StepButton
                index={0}
                currentStep={currentStep}
                info={
                  <ul>
                    <li>
                      <strong>{t("common.level1wrs")}:</strong>{" "}
                      {balanceAdjustmentData.level1Resource?.name}
                    </li>
                  </ul>
                }
              >
                {t("balance_adjustment.select_level1wrs")}
              </StepButton>
            ),
            panel: (
              <CreateBalanceAdjustmentSelectScheme
                onSave={stepHelpers.goToNextStep}
                onGoBack={stepHelpers.goToPrevStep}
              />
            ),
            infoPanel: (
              <InfoPanel>
                <div className="space-y-2">
                  <p>{t("balance_adjustment.create.select_level1wrs_info1")}</p>
                </div>
              </InfoPanel>
            ),
          },
          {
            label: (
              <StepButton
                index={1}
                currentStep={currentStep}
                info={
                  <ul>
                    <li>
                      <strong>{t("common.subscriber")}:</strong>{" "}
                      {balanceAdjustmentData.subscriber?.name}
                    </li>
                  </ul>
                }
              >
                {t("balance_adjustment.select_subscriber")}
              </StepButton>
            ),
            panel: (
              <CreateBalanceAdjustmentSelectSubscriber
                onSave={stepHelpers.goToNextStep}
                onGoBack={stepHelpers.goToPrevStep}
              />
            ),
            infoPanel: (
              <InfoPanel>
                <div className="space-y-2">
                  <p>
                    {t("balance_adjustment.create.select_subscriber_info1")}
                  </p>
                </div>
              </InfoPanel>
            ),
          },
          {
            label: (
              <StepButton
                index={2}
                currentStep={currentStep}
                info={
                  <ul>
                    <li>
                      <strong>{t("common.level0wrs")}:</strong>{" "}
                      {
                        balanceAdjustmentData.extractionRight?.level0Resource
                          ?.identifier
                      }
                    </li>
                  </ul>
                }
              >
                {t("balance_adjustment.select_extraction_right")}
              </StepButton>
            ),
            panel: (
              <CreateBalanceAdjustmentSelectExtractionRight
                onSave={stepHelpers.goToNextStep}
                onGoBack={stepHelpers.goToPrevStep}
              />
            ),
            infoPanel: (
              <InfoPanel>
                <div className="space-y-2">
                  <p>
                    {t(
                      "balance_adjustment.create.select_extraction_right_info1",
                    )}
                  </p>
                </div>
              </InfoPanel>
            ),
          },
          {
            label: (
              <StepButton
                index={3}
                currentStep={currentStep}
                info={
                  <ul>
                    <li>
                      <strong>{t("common.meter")}:</strong>{" "}
                      {balanceAdjustmentData.declarations
                        ?.map(d => d.meter?.serialNo)
                        .join(", ")}
                    </li>
                  </ul>
                }
              >
                {t("balance_adjustment.select_extraction_point")}
              </StepButton>
            ),
            panel: (
              <CreateBalanceAdjustmentSelectExtractionPoint
                onSave={stepHelpers.goToNextStep}
                onGoBack={stepHelpers.goToPrevStep}
              />
            ),
            infoPanel: (
              <InfoPanel>
                <div className="space-y-2">
                  <p>
                    {t(
                      "balance_adjustment.create.select_extraction_point_info1",
                    )}
                  </p>
                </div>
              </InfoPanel>
            ),
          },
          {
            label: (
              <StepButton
                index={4}
                currentStep={currentStep}
                info={
                  <ul>
                    <li>
                      <strong>
                        {t("balance_adjustment.create.adjustment")}:
                      </strong>{" "}
                      {sumBy(
                        balanceAdjustmentData.declarations,
                        (d: any) => d.adjustment,
                      ) > 0 && "+"}
                      {formatVolume(
                        sumBy(balanceAdjustmentData.declarations, "adjustment"),
                      )}
                    </li>
                  </ul>
                }
              >
                {t("balance_adjustment.create.title_harvesting")}
              </StepButton>
            ),
            panel: (
              <CreateBalanceAdjustmentEnterData
                onSave={stepHelpers.goToNextStep}
                onGoBack={stepHelpers.goToPrevStep}
              />
            ),
            infoPanel: (
              <InfoPanel>
                <div className="space-y-2">
                  <p>{t("balance_adjustment.create.enter_adjustment_info1")}</p>
                  <p>{t("balance_adjustment.create.enter_adjustment_info2")}</p>
                  <ul className="list-disc pl-4">
                    <li>
                      {t("balance_adjustment.create.enter_adjustment_info3")}
                    </li>
                    <li>
                      {t("balance_adjustment.create.enter_adjustment_info4")}
                    </li>
                    <li>
                      {t("balance_adjustment.create.enter_adjustment_info5")}
                    </li>
                  </ul>
                </div>
              </InfoPanel>
            ),
          },
          {
            label: (
              <StepButton
                index={5}
                currentStep={currentStep}
                info={
                  <ul>
                    <li>
                      <strong>
                        {t("balance_adjustment.create.file_field")}:
                      </strong>{" "}
                      {balanceAdjustmentData.file?.name || "-"}
                    </li>
                  </ul>
                }
              >
                {t("balance_adjustment.upload_file")}
              </StepButton>
            ),
            panel: (
              <CreateBalanceAdjustmentUploadEvidence
                onSave={stepHelpers.goToNextStep}
                onGoBack={stepHelpers.goToPrevStep}
              />
            ),
            infoPanel: (
              <InfoPanel>
                <div className="space-y-2">
                  <p>{t("balance_adjustment.create.upload_file_info1")}</p>
                  <p>{t("balance_adjustment.create.upload_file_info2")}</p>
                  <p>{t("balance_adjustment.create.upload_file_info3")}</p>
                </div>
              </InfoPanel>
            ),
          },
          {
            label: (
              <StepButton index={6} currentStep={currentStep}>
                {t("balance_adjustment.ledger")}
              </StepButton>
            ),
            panel: (
              <CreateBalanceAdjustmentLedger
                onSave={stepHelpers.goToNextStep}
                onGoBack={stepHelpers.goToPrevStep}
              />
            ),
            infoPanel: (
              <InfoPanel
                successes={[t("balance_adjustment.create.ledger_info1")]}
              />
            ),
          },
          {
            label: (
              <StepButton index={7} currentStep={currentStep}>
                {t("common.ledger")}
              </StepButton>
            ),
            panel: <CreateBalanceAdjustmentCompleted />,
            infoPanel: (
              <InfoPanel
                successes={[
                  t("balance_adjustment.create.completed_info1", {
                    adjustment:
                      (sumBy(
                        balanceAdjustmentData.declarations,
                        (d: any) => d.adjustment,
                      ) > 0
                        ? "+"
                        : "") +
                      formatVolume(
                        sumBy(balanceAdjustmentData.declarations, "adjustment"),
                      ),
                    level0ResourceIdentifier:
                      balanceAdjustmentData.extractionRight?.level0Resource
                        ?.identifier,
                    subscriberName: balanceAdjustmentData.subscriber?.name,
                    subscriberAccountNumber:
                      balanceAdjustmentData.subscriber?.accountNumber,
                  }),
                ]}
              />
            ),
          },
        ]}
      />
    </Layout>
  );
};

export default CreateBalanceAdjustment;
